import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Trophy, Users, BarChart3, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSiteConfig } from '../../hooks/useSiteConfig';
import { themeClasses } from '../../utils/themeClasses';

const HeroSection: React.FC = () => {
  const siteConfig = useSiteConfig();
  const stats = [
    { label: 'Championships', value: '150+', icon: Trophy },
    { label: 'Teams Tracked', value: '500+', icon: Users },
    { label: 'Data Points', value: '1M+', icon: BarChart3 },
    { label: 'Live Updates', value: '24/7', icon: Zap },
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden py-20">
      {/* Animated Background */}
      <div className="absolute inset-0">
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-grid-pattern opacity-20" />
        
        {/* Animated Gradient Orbs */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[100px]"
          animate={{
            x: [0, 100, 0],
            y: [0, -100, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-secondary/10 rounded-full blur-[100px]"
          animate={{
            x: [0, -100, 0],
            y: [0, 100, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent/5 rounded-full blur-[120px]"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full border border-primary/30 mb-8"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
            </span>
            <span className="text-primary text-xs font-semibold uppercase tracking-wider">Real-Time Sports Data & Analytics</span>
          </motion.div>

          {/* Main Heading */}
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black leading-[0.9] tracking-tight">
              {siteConfig.sport ? (
                // Sport-specific heading
                <>
                  <span className="block text-white mb-2">EVERY</span>
                  <span className={`block ${themeClasses.gradientText} mb-2`} style={{ fontSize: '1.1em' }}>
                    {siteConfig.sport === 'football' && siteConfig.level === 'college' ? 'COLLEGE FOOTBALL' :
                     siteConfig.sport === 'basketball' && siteConfig.level === 'college' ? 'MARCH MADNESS' :
                     siteConfig.sport === 'baseball' && siteConfig.level === 'college' ? 'COLLEGE WORLD SERIES' :
                     siteConfig.sport === 'hockey' && siteConfig.level === 'college' ? 'FROZEN FOUR' :
                     siteConfig.sport === 'football' ? 'PRO FOOTBALL' :
                     siteConfig.sport === 'basketball' ? 'PRO BASKETBALL' :
                     siteConfig.sport === 'baseball' ? 'PRO BASEBALL' :
                     siteConfig.sport === 'hockey' ? 'PRO HOCKEY' :
                     'CHAMPIONSHIP'}
                  </span>
                  <span className="block text-white mb-2">CHAMPION</span>
                  <span className={`block ${themeClasses.neonText}`} style={{ fontSize: '1.2em' }}>DOCUMENTED</span>
                </>
              ) : (
                // Main site heading
                <>
                  <span className="block text-white mb-2">LET YOUR</span>
                  <span className={`block ${themeClasses.gradientText} mb-2`} style={{ fontSize: '1.1em' }}>SPORTS DATA FLY</span>
                  <span className="block text-white mb-2">WITH NEXT-LEVEL</span>
                  <span className={`block ${themeClasses.neonText}`} style={{ fontSize: '1.2em' }}>ANALYTICS</span>
                </>
              )}
            </h1>
          </motion.div>

          {/* Subtitle */}
          <motion.p
            className="text-lg md:text-xl text-gray-400 mb-12 max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            {siteConfig.sport ? siteConfig.description : 
             'Access comprehensive championship data, team statistics, and historical records from professional and college sports—all in one powerful platform.'}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Link 
              to="/championships" 
              className="explore-championships-btn group flex items-center space-x-2 px-8 py-3 bg-primary text-black font-bold text-sm uppercase tracking-wider rounded-lg hover:scale-105 transition-all duration-300 shadow-[0_0_30px_rgba(0,255,136,0.5)]"
            >
              <span>EXPLORE CHAMPIONSHIPS</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <button className="start-now-btn flex items-center space-x-2 px-8 py-3 font-bold text-sm uppercase tracking-wider rounded-lg transition-all duration-300">
              <Trophy className="w-4 h-4" />
              <span>START NOW</span>
            </button>
          </motion.div>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-24 max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.label}
                className="relative group"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 + index * 0.1 }}
              >
                <div className="absolute inset-0 bg-primary/5 rounded-xl blur-xl group-hover:bg-primary/10 transition-colors" />
                <div className="relative glass-card p-6 text-center hover:scale-105 transition-transform duration-300 border-primary/20 hover:border-primary/40">
                  <Icon className="w-6 h-6 text-primary mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">{stat.label}</div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-5 h-8 border border-primary/50 rounded-full flex justify-center">
          <motion.div 
            className="w-1 h-2 bg-primary rounded-full mt-1"
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;